import { useState, useEffect, useCallback } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  useMap
} from '@vis.gl/react-google-maps';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Info,
  Search,
  Loader2,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
const DEFAULT_CENTER = { lat: 51.5074, lng: -0.1278 };

const ENFORCEMENT_MODES = {
  off: {
    label: 'Off',
    description: 'Location captured but not validated',
    icon: ShieldOff,
    color: 'bg-gray-100 text-gray-700 border-gray-300'
  },
  warning: {
    label: 'Warning',
    description: 'Allow check-in outside geofence with warning',
    icon: ShieldAlert,
    color: 'bg-yellow-100 text-yellow-700 border-yellow-300'
  },
  strict: {
    label: 'Strict',
    description: 'Block check-ins outside geofence',
    icon: ShieldCheck,
    color: 'bg-red-100 text-red-700 border-red-300'
  }
};

const RADIUS_PRESETS = [
  { value: 50, label: '50m', description: 'Building entrance' },
  { value: 100, label: '100m', description: 'Small site' },
  { value: 200, label: '200m', description: 'Medium site' },
  { value: 500, label: '500m', description: 'Large site' },
  { value: 1000, label: '1km', description: 'Campus' }
];

// Circle overlay component for geofence radius visualization
function GeofenceCircle({ center, radius }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !center || !radius) return;

    const circle = new window.google.maps.Circle({
      map,
      center,
      radius,
      fillColor: '#3b82f6',
      fillOpacity: 0.15,
      strokeColor: '#3b82f6',
      strokeOpacity: 0.8,
      strokeWeight: 2
    });

    return () => circle.setMap(null);
  }, [map, center, radius]);

  return null;
}

// Places search component
function PlacesSearch({ onPlaceSelect }) {
  const map = useMap();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const handleSearch = useCallback((q) => {
    setQuery(q);
    if (!q.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setSearching(true);
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input: q, componentRestrictions: { country: 'uk' } },
      (predictions, status) => {
        setSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setResults(predictions || []);
          setShowResults(true);
        }
      }
    );
  }, []);

  const handleSelect = useCallback((placeId) => {
    if (!map) return;
    const service = new window.google.maps.places.PlacesService(map);
    service.getDetails(
      { placeId, fields: ['formatted_address', 'geometry', 'name'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const coords = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          onPlaceSelect(coords, place.formatted_address || place.name);
          setQuery(place.formatted_address || place.name);
          setShowResults(false);
        }
      }
    );
  }, [map, onPlaceSelect]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search for location..."
          className="pl-9"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
        )}
      </div>
      {showResults && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border max-h-60 overflow-y-auto">
          {results.map((result) => (
            <button
              key={result.place_id}
              onClick={() => handleSelect(result.place_id)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 border-b last:border-b-0"
            >
              <div className="font-medium">{result.structured_formatting?.main_text}</div>
              <div className="text-xs text-gray-500">{result.structured_formatting?.secondary_text}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Map content with marker and circle
function MapContent({ center, radius, onCenterChange }) {
  const map = useMap();

  // Fit bounds to show the geofence circle
  useEffect(() => {
    if (!map || !center || !radius) return;

    const circle = new window.google.maps.Circle({ center, radius });
    const bounds = circle.getBounds();
    if (bounds) {
      map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
    }
  }, [map, center, radius]);

  const handleDragEnd = useCallback((e) => {
    if (!e.latLng) return;
    onCenterChange({
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    });
  }, [onCenterChange]);

  return (
    <>
      {center && (
        <>
          <AdvancedMarker
            position={center}
            draggable={true}
            onDragEnd={handleDragEnd}
            title="Geofence center - drag to move"
          >
            <div className="relative">
              <Pin background="#3b82f6" borderColor="#fff" glyphColor="#fff" />
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-2 py-0.5 rounded text-xs whitespace-nowrap">
                <Target className="h-3 w-3 inline mr-1" />
                Center
              </div>
            </div>
          </AdvancedMarker>
          <GeofenceCircle center={center} radius={radius} />
        </>
      )}
    </>
  );
}

export function GeofenceSettings({ siteLocationId, initialData, onSave }) {
  const [enabled, setEnabled] = useState(initialData?.geofence_enabled || false);
  const [enforcement, setEnforcement] = useState(initialData?.geofence_enforcement || 'off');
  const [radius, setRadius] = useState(initialData?.geofence_radius_meters || 100);
  const [center, setCenter] = useState(() => {
    if (initialData?.latitude && initialData?.longitude) {
      return {
        lat: parseFloat(initialData.latitude),
        lng: parseFloat(initialData.longitude)
      };
    }
    return null;
  });
  const [address, setAddress] = useState(initialData?.address || '');
  const [saving, setSaving] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Sync state when initialData loads/changes (handles async loading)
  useEffect(() => {
    if (initialData && !initialized) {
      setEnabled(initialData.geofence_enabled || false);
      setEnforcement(initialData.geofence_enforcement || 'off');
      setRadius(initialData.geofence_radius_meters || 100);
      if (initialData.latitude && initialData.longitude) {
        setCenter({
          lat: parseFloat(initialData.latitude),
          lng: parseFloat(initialData.longitude)
        });
      }
      setAddress(initialData.address || '');
      setInitialized(true);
    }
  }, [initialData, initialized]);

  // Handle toggle with confirmation for disabling
  const handleToggle = useCallback((checked) => {
    if (!checked && enabled) {
      // User is trying to disable - show confirmation
      setShowDisableConfirm(true);
    } else {
      setEnabled(checked);
    }
  }, [enabled]);

  const confirmDisable = useCallback(() => {
    setEnabled(false);
    setShowDisableConfirm(false);
  }, []);

  const handlePlaceSelect = useCallback((coords, addr) => {
    setCenter(coords);
    setAddress(addr);
  }, []);

  const handleSave = async () => {
    if (enabled && !center) {
      toast.error('Please set a location for the geofence center');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('site_locations')
        .update({
          geofence_enabled: enabled,
          geofence_enforcement: enforcement,
          geofence_radius_meters: radius,
          latitude: center?.lat || null,
          longitude: center?.lng || null,
          address: address || null
        })
        .eq('id', siteLocationId);

      if (error) throw error;

      toast.success('Geofence settings saved');
      onSave?.();
    } catch (error) {
      console.error('Error saving geofence settings:', error);
      toast.error('Failed to save geofence settings');
    } finally {
      setSaving(false);
    }
  };

  const EnforcementIcon = ENFORCEMENT_MODES[enforcement]?.icon || Shield;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            <CardTitle>Geofence Settings</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="geofence-toggle" className="text-sm text-gray-600">
              {enabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id="geofence-toggle"
              checked={enabled}
              onCheckedChange={handleToggle}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {enabled ? (
          <>
            {/* Enforcement Mode */}
            <div className="space-y-2">
              <Label>Enforcement Mode</Label>
              <Select value={enforcement} onValueChange={setEnforcement}>
                <SelectTrigger>
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <EnforcementIcon className="h-4 w-4" />
                      {ENFORCEMENT_MODES[enforcement]?.label}
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ENFORCEMENT_MODES).map(([key, mode]) => {
                    const Icon = mode.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span>{mode.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {ENFORCEMENT_MODES[enforcement]?.description}
              </p>
            </div>

            {/* Radius Configuration */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Geofence Radius</Label>
                <Badge variant="outline" className="text-blue-600 border-blue-300">
                  {radius}m
                </Badge>
              </div>
              <Slider
                value={[radius]}
                onValueChange={([val]) => setRadius(val)}
                min={25}
                max={2000}
                step={25}
              />
              <div className="flex flex-wrap gap-2">
                {RADIUS_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={radius === preset.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setRadius(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Map Location Picker */}
            <div className="space-y-2">
              <Label>Geofence Center Location</Label>
              {GOOGLE_MAPS_API_KEY ? (
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
                  <div className="space-y-2">
                    <PlacesSearch onPlaceSelect={handlePlaceSelect} />
                    <div className="h-[300px] rounded-lg overflow-hidden border">
                      <Map
                        defaultCenter={center || DEFAULT_CENTER}
                        defaultZoom={15}
                        mapId="geofence-map"
                        gestureHandling="greedy"
                        disableDefaultUI={true}
                        zoomControl={true}
                        onClick={(e) => {
                          if (e.detail?.latLng) {
                            setCenter({
                              lat: e.detail.latLng.lat,
                              lng: e.detail.latLng.lng
                            });
                          }
                        }}
                      >
                        <MapContent
                          center={center}
                          radius={radius}
                          onCenterChange={setCenter}
                        />
                      </Map>
                    </div>
                    {center && (
                      <p className="text-xs text-gray-500">
                        Coordinates: {center.lat.toFixed(6)}, {center.lng.toFixed(6)}
                      </p>
                    )}
                  </div>
                </APIProvider>
              ) : (
                <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500">
                  <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>Google Maps API key not configured</p>
                  <p className="text-xs mt-1">Set VITE_GOOGLE_MAPS_API_KEY in .env</p>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">How Geofencing Works</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700 text-xs">
                    <li>Users are prompted for location permission on check-in</li>
                    <li>Their device location is validated against the geofence</li>
                    <li>Background monitoring sends notifications if user leaves</li>
                    <li>No automatic check-out - notifications only</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Geofencing is disabled for this site</p>
            <p className="text-sm mt-1">Enable to restrict check-ins to a specific area</p>
          </div>
        )}

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Geofence Settings'
          )}
        </Button>
      </CardContent>

      {/* Disable Confirmation Dialog */}
      <AlertDialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-yellow-600" />
              Disable Geofencing?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Disabling geofencing will stop location verification for check-ins.
              Users will be able to check in from any location without validation.
              <br /><br />
              You can re-enable it at any time without losing your configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisable}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Disable Geofencing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default GeofenceSettings;
